'use client';

import { parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';

import { Snackbar } from '../Snackbar';

import type { AuthLayoutAuthError } from '../AuthLayout/AuthLayout';

type AuthErrorLoggerProps = {
  initialAuthError?: AuthLayoutAuthError | null;
  onError?: (error: AuthLayoutAuthError) => void;
};

export const AuthErrorLogger = ({ initialAuthError = null, onError }: AuthErrorLoggerProps) => {
  const [authError, setAuthError] = useState<AuthLayoutAuthError | null>(initialAuthError);
  const [isVisible, setIsVisible] = useState(Boolean(initialAuthError));
  const [{ error: errorCode, error_description: errorDetail }] = useQueryStates({
    error: parseAsString,
    error_description: parseAsString,
  });

  useEffect(() => {
    const handleError = (nextError: AuthLayoutAuthError) => {
      const errorPayload = {
        code: nextError.code,
        detail: nextError.detail,
        message: nextError.message,
      };

      window.dispatchEvent(new CustomEvent('auth-error', { detail: errorPayload }));
      // eslint-disable-next-line no-console
      console.error('SSO sign-in failed', errorPayload);
      onError?.(errorPayload);
    };

    if (initialAuthError) {
      setAuthError(initialAuthError);
      setIsVisible(true);
      handleError(initialAuthError);

      return;
    }

    if (!errorCode && !errorDetail) {
      return;
    }

    const nextError = {
      code: errorCode ?? 'signin_oauth_error',
      detail: errorDetail ?? 'The sign-in provider returned an error.',
      message: 'There is an issue with SSO. Please try again later or contact support if the problem continues.',
    };

    setAuthError(nextError);
    setIsVisible(true);
    handleError(nextError);
  }, [errorCode, errorDetail, initialAuthError, onError]);

  if (!authError || !isVisible) {
    return null;
  }

  return (
    <Snackbar
      color="white"
      isOpen={isVisible}
      message={authError.message}
      setOpen={(open) => {
        setIsVisible(open);
        setAuthError(open ? authError : null);
      }}
      title="We hit a problem"
      variant="banner"
    />
  );
};
